import { formatMessage } from "../../../lib/intlUtils";
import { BillingEntity } from "../domain/Billing.entity";
import { billingReportMessages } from "../messages";
import Papa from "papaparse";
import { OptionType } from "../index";

interface BillingReportCellDefinition {
  name: string;
  message: { id: string; defaultMessage: string };
  type: "string" | "number";
  isWitAdmin: boolean;
  isSepteo?: boolean;
  isCsvOnly?: boolean;
  value: (entity: BillingEntity, locale: string) => string;
}

export type BillingReportHeader = {
  name: string;
  title: string;
  type: "string" | "number";
};

export type BillingReportRow = {
  type: "string" | "number";
  value: string;
}[];

const cellDefinitions: BillingReportCellDefinition[] = [
  {
    name: "invoiceNumber",
    message: billingReportMessages.tableColumnInvoiceNumber,
    type: "string",
    isWitAdmin: false,
    value: entity => entity.invoiceNumber,
  },
  {
    name: "idMyS",
    message: billingReportMessages.tableColumnIdMyS,
    type: "number",
    isWitAdmin: true,
    isSepteo: true,
    value: entity => entity.idMySFormatted(),
  },
  {
    name: "tax",
    message: billingReportMessages.tableColumnTax,
    type: "number",
    isWitAdmin: true,
    isSepteo: true,
    value: entity => entity.taxFormatted(),
  },
  {
    name: "fp",
    message: billingReportMessages.tableColumnFP,
    type: "string",
    isWitAdmin: true,
    isSepteo: true,
    value: entity => entity.fp,
  },
  {
    name: "legalName",
    message: billingReportMessages.tableColumnLegalName,
    type: "string",
    isWitAdmin: true,
    isSepteo: true,
    value: entity => entity.legalName,
  },
  {
    name: "cif",
    message: billingReportMessages.tableColumnCIF,
    type: "string",
    isWitAdmin: true,
    isSepteo: true,
    value: entity => entity.cif,
  },
  {
    name: "ticker",
    message: billingReportMessages.tableColumnDomain,
    type: "string",
    isWitAdmin: false,
    value: entity => entity.ticker,
  },
  {
    name: "partner",
    message: billingReportMessages.tableColumnPartner,
    type: "string",
    isWitAdmin: true,
    isSepteo: true,
    value: entity => entity.partner,
  },
  {
    name: "concept",
    message: billingReportMessages.tableColumnConcept,
    type: "string",
    isWitAdmin: false,
    value: entity => entity.concept,
  },
  {
    name: "period",
    message: billingReportMessages.tableColumnPeriod,
    type: "string",
    isWitAdmin: false,
    value: entity => entity.period,
  },
  {
    name: "unitPrice",
    message: billingReportMessages.tableColumnUnitPrice,
    type: "number",
    isWitAdmin: false,
    value: (entity, locale) => entity.priceUnitFormatted(locale),
  },
  {
    name: "unitPricePerClient",
    message: billingReportMessages.unitPricePerClient,
    type: "string",
    isWitAdmin: true,
    isSepteo: true,
    isCsvOnly: true,
    value: (entity, locale) => entity.fullConceptFormatted(locale),
  },
  {
    name: "units",
    message: billingReportMessages.tableColumnUnits,
    type: "number",
    isWitAdmin: false,
    value: (entity, locale) => entity.unitsFormatted(locale),
  },
  {
    name: "rate",
    message: billingReportMessages.tableColumnRate,
    type: "number",
    isWitAdmin: false,
    value: (entity, locale) => entity.rateFormatted(locale),
  },
  {
    name: "discount",
    message: billingReportMessages.tableColumnDiscount,
    type: "number",
    isWitAdmin: false,
    value: (entity, locale) => entity.discountFormatted(locale),
  },
  {
    name: "totalRate",
    message: billingReportMessages.tableColumnTotalRate,
    type: "number",
    isWitAdmin: false,
    value: (entity, locale) => entity.totalRateFormatted(locale),
  },
  {
    name: "currency",
    message: billingReportMessages.tableColumnCurrency,
    type: "string",
    isWitAdmin: false,
    value: entity => entity.currency,
  },
  {
    name: "currencyChange",
    message: billingReportMessages.tableColumnCurrencyChange,
    type: "number",
    isWitAdmin: true,
    value: (entity, locale) => entity.currencyChangeFormatted(locale),
  },
  {
    name: "percentagePartner",
    message: billingReportMessages.tableColumnPercentagePartner,
    type: "number",
    isWitAdmin: true,
    isSepteo: true,
    value: (entity, locale) => entity.percentagePartnerFormatted(locale),
  },
  {
    name: "invoicePartner",
    message: billingReportMessages.tableColumnInvoicePartner,
    type: "number",
    isWitAdmin: true,
    isSepteo: true,
    value: (entity, _) => entity.invoicePartnerFormatted(),
  },
  {
    name: "totalRateInvoice",
    message: billingReportMessages.tableColumnTotalRateInvoice,
    type: "string",
    isWitAdmin: true,
    isSepteo: true,
    value: (entity, locale) => entity.totalRateInvoiceFormatted(locale),
  },
  {
    name: "totalRateMargin",
    message: billingReportMessages.tableColumnTotalRateMargin,
    type: "number",
    isWitAdmin: true,
    value: (entity, locale) => entity.totalRateMarginFormatted(locale),
  },
  {
    name: "fullConcept",
    message: billingReportMessages.tableColumnFullConcept,
    type: "string",
    isWitAdmin: true,
    isSepteo: true,
    isCsvOnly: true,
    value: (entity, locale) => entity.fullConceptFormatted(locale),
  },
];

function getCellDefinitions({
  isWitAdmin,
  isSepteo,
  isCSV,
}: {
  isWitAdmin: boolean;
  isSepteo: boolean;
  isCSV?: boolean;
}) {
  if (isWitAdmin) {
    if (!isCSV) {
      return cellDefinitions.filter(it => !it.isCsvOnly);
    }
    return cellDefinitions;
  }
  if (isSepteo) {
    if (!isCSV) {
      return cellDefinitions.filter(it => (!it.isWitAdmin || it.isSepteo) && !it.isCsvOnly);
    }
    return cellDefinitions.filter(it => !it.isWitAdmin || it.isSepteo);
  }
  if (!isCSV) {
    return cellDefinitions.filter(it => !it.isWitAdmin && !it.isSepteo && !it.isCsvOnly);
  }
  return cellDefinitions.filter(it => !it.isWitAdmin && !it.isSepteo);
}

export function generateBillingReportHeaders(
  locale: string,
  isWitAdmin: boolean,
  isSepteo: boolean
): BillingReportHeader[] {
  const definitions = getCellDefinitions({ isWitAdmin, isSepteo });
  return definitions.map(definition => ({
    name: definition.name,
    title: formatMessage(definition.message, locale),
    type: definition.type,
  }));
}

export function generateBillingReportRows(
  billings: BillingEntity[],
  locale: string,
  isWitAdmin: boolean,
  isSepteo: boolean
): BillingReportRow[] {
  const definitions = getCellDefinitions({ isWitAdmin, isSepteo });
  return billings.map(billing =>
    definitions.map(definition => ({
      value: definition.value(billing, locale),
      type: definition.type,
    }))
  );
}

export function generateBillingReportCsv({
  billings,
  locale,
  isWitAdmin,
  period,
  isSepteo,
  onlyVariables,
}: {
  billings: BillingEntity[];
  locale: string;
  isWitAdmin: boolean;
  period: OptionType;
  isSepteo: boolean;
  onlyVariables?: boolean;
}): void {
  const definitions = getCellDefinitions({ isWitAdmin, isSepteo, isCSV: true });
  const filteredBillings = onlyVariables ? billings.filter(it => it.isVariable) : billings;
  const unitPricePerClient = filteredBillings.reduce((acc, billing) => {
    if (!acc[billing.invoiceNumber]) {
      acc[billing.invoiceNumber] = 0;
    }
    acc[billing.invoiceNumber] += billing.priceUnit;
    return acc;
  }, {} as Record<string, number>);
  const billingDTOs = filteredBillings.map((billing: BillingEntity) => {
    const obj: Record<string, string> = {};
    definitions.forEach(it => {
      if (it.name === "unitPricePerClient") {
        obj[formatMessage(it.message, locale)] = billing.priceUnitPerClientFormatted(
          unitPricePerClient[billing.invoiceNumber],
          locale
        );
        return;
      }
      obj[formatMessage(it.message, locale)] = it.value(billing, locale);
    });
    return obj;
  });
  const csv = Papa.unparse(billingDTOs, { delimiter: ";", quotes: true });
  const bom = "\uFEFF"; // UTF-8 BOM to support special characters
  const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `Facturacion_${period.value}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
